/**
 * Состояние парка: первый снимок запросом, дальше живая лента.
 *
 * Лента — основной путь. Опрос остаётся запасным и включается только когда
 * ленты нет: обрыв канала, перезапуск сервера, прокси, режущий веб-сокеты.
 * Диспетчер в этом случае увидит данные с задержкой, но увидит.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { ApiError, api, type Device, type EventRow, type Position } from './api'

/** Как часто опрашивать, пока живой ленты нет. */
const POLL_MS = 5000

/** Паузы перед повторным подключением, миллисекунды. Дальше — по последней. */
const BACKOFF_MS = [1000, 2000, 5000, 10000, 20000]

/** Код закрытия, которым прослойка сообщает, что сессии нет. */
const CLOSED_NO_SESSION = 1008

/** Сколько последних точек держать для живого следа за машиной. */
const TAIL_POINTS = 24

type Snapshot = {
  devices: Device[]
  positions: Map<number, Position>
  /** Живая лента подключена. Ложь означает работу на опросе. */
  live: boolean
  /** Данные получить не удалось вовсе. */
  failed: boolean
  /**
   * Последние точки по каждой машине — короткий след за ней на карте.
   *
   * Копится из живой ленты и живёт только в этой вкладке: это не история, а
   * подсказка «откуда она приехала», и переживать перезагрузку ей незачем.
   */
  tails: Map<number, Position[]>
  /**
   * Перечитать список немедленно.
   *
   * Нужен управлению: заведённая или удалённая машина должна появиться в
   * списке и на карте сразу, а не через следующий опрос. Живая лента про
   * такие изменения не рассказывает — она про движение, а не про состав парка.
   */
  reload: () => void
}

export function useFleet(
  onUnauthorized: () => void,
  onEvents?: (events: EventRow[]) => void,
): Snapshot {
  const [devices, setDevices] = useState<Device[]>([])
  const [positions, setPositions] = useState(new Map<number, Position>())
  const [live, setLive] = useState(false)
  const [failed, setFailed] = useState(false)
  const [tails, setTails] = useState(new Map<number, Position[]>())

  // Обработчик выхода держим в ссылке: пересоздавать из-за него соединение
  // означало бы рвать ленту при каждой перерисовке.
  const unauthorizedRef = useRef(onUnauthorized)
  unauthorizedRef.current = onUnauthorized

  // События приходят той же лентой, что и позиции, но живут отдельной жизнью:
  // их не накапливают в состоянии, а сразу отдают наружу — показать и забыть.
  const eventsRef = useRef(onEvents)
  eventsRef.current = onEvents

  const loadSnapshot = useCallback(async () => {
    try {
      const [list, points] = await Promise.all([api.devices(), api.positions()])
      setDevices(list)
      setPositions(new Map(points.map((p) => [p.deviceId, p])))
      setFailed(false)
      return true
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) {
        unauthorizedRef.current()
        return false
      }
      setFailed(true)
      return false
    }
  }, [])

  // Первый снимок и опрос, пока ленты нет.
  useEffect(() => {
    void loadSnapshot()
  }, [loadSnapshot])

  useEffect(() => {
    if (live) return
    const timer = setInterval(() => void loadSnapshot(), POLL_MS)
    return () => clearInterval(timer)
  }, [live, loadSnapshot])

  // Живая лента.
  useEffect(() => {
    let socket: WebSocket | null = null
    let retry = 0
    let timer: number | undefined
    let stopped = false

    function connect() {
      if (stopped) return
      const scheme = location.protocol === 'https:' ? 'wss' : 'ws'
      socket = new WebSocket(`${scheme}://${location.host}/api/live`)

      socket.onopen = () => {
        retry = 0
      }

      socket.onmessage = (event) => {
        let payload: unknown
        try {
          payload = JSON.parse(event.data as string)
        } catch {
          return // мусор в ленте не должен ронять экран
        }
        if (!payload || typeof payload !== 'object') return

        // Прослойка подтверждает, что канал до Traccar открыт. До этого
        // момента лента живой не считается — иначе опрос выключился бы рано.
        if ((payload as { type?: string }).type === 'ready') {
          setLive(true)
          setFailed(false)
          void loadSnapshot() // догоняем то, что случилось до подключения
          return
        }

        const message = payload as {
          devices?: Device[]
          positions?: Position[]
          events?: EventRow[]
        }

        if (message.events?.length) eventsRef.current?.(message.events)

        if (message.positions?.length) {
          setPositions((was) => {
            const next = new Map(was)
            for (const p of message.positions!) next.set(p.deviceId, p)
            return next
          })
          setTails((was) => {
            const next = new Map(was)
            for (const p of message.positions!) {
              const tail = [...(next.get(p.deviceId) ?? []), p]
              next.set(p.deviceId, tail.slice(-TAIL_POINTS))
            }
            return next
          })
        }

        if (message.devices?.length) {
          setDevices((was) => {
            const byId = new Map(was.map((d) => [d.id, d]))
            for (const d of message.devices!) byId.set(d.id, { ...byId.get(d.id), ...d })
            return [...byId.values()]
          })
        }
      }

      socket.onclose = (event) => {
        setLive(false)
        if (event.code === CLOSED_NO_SESSION) {
          unauthorizedRef.current()
          return
        }
        if (stopped) return
        const wait = BACKOFF_MS[Math.min(retry, BACKOFF_MS.length - 1)]!
        retry += 1
        timer = window.setTimeout(connect, wait)
      }

      socket.onerror = () => {
        // Само по себе не фатально: за ошибкой всегда следует onclose,
        // который и займётся повторным подключением.
      }
    }

    connect()

    return () => {
      stopped = true
      if (timer) clearTimeout(timer)
      socket?.close()
    }
  }, [loadSnapshot])

  const reload = useCallback(() => {
    void loadSnapshot()
  }, [loadSnapshot])

  return { devices, positions, live, failed, tails, reload }
}
